/**
 * Utility functions for RFID Device Management
 * Provides helper functions for device selection and MQTT integration
 */

// LocalStorage keys
export const STORAGE_KEYS = {
  SELECTED_DEVICES: 'selectedDevices',
  RFID_IMPORT_DEVICE: 'device-rfid-import',
  RFID_TRANSFER_DEVICE: 'device-rfid-transfer',
  RFID_AUDIT_DEVICE: 'device-rfid-audit'
};

/**
 * Get selected RFID device for a specific operation
 * @param {string} operation - The operation type (import, transfer, audit)
 * @returns {string} - The selected device ID
 */
export const getSelectedRfidDevice = (operation = 'import') => {
  const key = operation === 'import' ? STORAGE_KEYS.RFID_IMPORT_DEVICE :
              operation === 'transfer' ? STORAGE_KEYS.RFID_TRANSFER_DEVICE :
              operation === 'audit' ? STORAGE_KEYS.RFID_AUDIT_DEVICE :
              STORAGE_KEYS.RFID_IMPORT_DEVICE;
  
  try {
    return localStorage.getItem(key) || '';
  } catch (error) {
    console.error('Error getting selected RFID device:', error);
    return '';
  }
};

/**
 * Save selected RFID device for a specific operation
 * @param {string} deviceId - The device ID to save
 * @param {string} operation - The operation type (import, transfer, audit)
 * @returns {boolean} - Success status
 */
export const saveSelectedRfidDevice = (deviceId, operation = 'import') => {
  const key = operation === 'import' ? STORAGE_KEYS.RFID_IMPORT_DEVICE :
              operation === 'transfer' ? STORAGE_KEYS.RFID_TRANSFER_DEVICE :
              operation === 'audit' ? STORAGE_KEYS.RFID_AUDIT_DEVICE :
              STORAGE_KEYS.RFID_IMPORT_DEVICE;
  
  try {
    if (deviceId) {
      localStorage.setItem(key, deviceId);
    } else {
      localStorage.removeItem(key);
    }
    return true;
  } catch (error) {
    console.error('Error saving selected RFID device:', error);
    return false;
  }
};

/**
 * Clear selected RFID device for a specific operation
 * @param {string} operation - The operation type (import, transfer, audit)
 * @returns {boolean} - Success status
 */
export const clearSelectedRfidDevice = (operation = 'import') => {
  return saveSelectedRfidDevice('', operation);
};

/**
 * Get all selected devices from localStorage
 * @returns {Array} - Array of selected device IDs
 */
export const getSelectedDevices = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SELECTED_DEVICES);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error getting selected devices:', error);
    return [];
  }
};

/**
 * Check if a device is selected
 * @param {string} deviceId - The device ID to check
 * @returns {boolean} - Whether the device is selected
 */
export const isDeviceSelected = (deviceId) => {
  const selectedDevices = getSelectedDevices();
  return selectedDevices.includes(deviceId);
};

/**
 * Get device name by ID from available devices
 * @param {string} deviceId - The device ID
 * @param {Array} availableDevices - Array of available devices
 * @returns {string} - The device name or fallback
 */
export const getDeviceName = (deviceId, availableDevices = []) => {
  const device = availableDevices.find(d => d.id === deviceId);
  return device ? device.name : `Device ${deviceId}`;
};

/**
 * Create MQTT topic for device
 * @param {string} deviceId - The device ID
 * @param {string} prefix - Optional topic prefix
 * @returns {string} - The MQTT topic
 */
export const createDeviceTopic = (deviceId, prefix = '') => {
  return prefix ? `${prefix}/${deviceId}` : deviceId;
};

/**
 * Validate device selection
 * @param {string} deviceId - The device ID to validate
 * @param {Array} availableDevices - Array of available devices
 * @returns {Object} - Validation result with isValid and message
 */
export const validateDeviceSelection = (deviceId, availableDevices = []) => {
  if (!deviceId) {
    return { isValid: false, message: 'No device selected' };
  }
  
  if (!availableDevices.length) {
    return { isValid: false, message: 'No devices available' };
  }
  
  const deviceExists = availableDevices.some(d => d.id === deviceId);
  if (!deviceExists) {
    return { isValid: false, message: 'Selected device not found in available devices' };
  }
  
  return { isValid: true, message: 'Device selection is valid' };
};

/**
 * Test RFID device functionality
 * This function can be called from browser console for testing
 */
export const testRfidDeviceFunctionality = () => {
  // Testing RFID Device Functionality...
  
  // Test 1: Save device selection
  const testDeviceId = 'test-device-001';
  const success = saveSelectedRfidDevice(testDeviceId, 'import');
  // Save device selection test
  
  // Test 2: Get device selection
  const savedDevice = getSelectedRfidDevice('import');
  // Get device selection test
  
  // Test 3: Clear device selection
  const cleared = clearSelectedRfidDevice('import');
  // Clear device selection test
  
  // Test 4: Validate device selection
  const mockDevices = [
    { id: 'device1', name: 'RFID Reader 001' },
    { id: 'device2', name: 'RFID Reader 002' }
  ];
  const validation = validateDeviceSelection('device1', mockDevices);
  // Validate device selection test
  
  // All tests passed! ✅
};

// Export for browser console testing
if (typeof window !== 'undefined') {
  window.testRfidDeviceFunctionality = testRfidDeviceFunctionality;
}

export default {
  getSelectedRfidDevice,
  saveSelectedRfidDevice,
  clearSelectedRfidDevice,
  getSelectedDevices,
  isDeviceSelected,
  getDeviceName,
  createDeviceTopic,
  validateDeviceSelection,
  STORAGE_KEYS,
  testRfidDeviceFunctionality
};
